/**
 * RowEqualizer (E20-1060, extended by E20-1460)
 *
 * Aligns every region of the reusable Product Card across each row, so each one starts at a
 * common vertical offset and cards end level — even when ratings, descriptions, bullets, JRNY
 * designations and promo messages differ from card to card.
 *
 * Regions default to `DEFAULT_REGIONS` below; override per placement with `data-equalize`
 * (comma-separated selectors). Name and description are equalized separately rather than via
 * their shared `__link` wrapper, so a name that wraps to two lines can't push a neighbour's
 * description out of line. Cards default to `.product-card`, overridable with `data-cards`.
 *
 * It only sets `min-height` on the targeted sub-elements — never the card's own styling — and
 * resets before each measure so a breakpoint change re-measures natural heights.
 *
 * The card renders a container for every optional region even when the product has no content
 * for it (E20-1460); the SCSS collapses an empty one via `:empty`. This adds RESERVED_CLASS to
 * un-hide the ones it pads out, so a region present on at least one card holds its space on all
 * of them, while a region no card fills stays collapsed everywhere.
 *
 * Rows are detected from each card's top edge measured against this container, so the grouping
 * re-forms as the column count changes across breakpoints (4 → 3 → 2) and doesn't depend on
 * which ancestor is the offsetParent. Every cell in a horizontal carousel track shares one top,
 * so the whole track becomes a single group — heights are set for all cards, not just the
 * visible page, and paging can't change them.
 *
 * Mirrors the observer/scheduling conventions of bfx-compare-heights.js, but groups rows
 * dynamically (by offset) rather than by a static data attribute, which a fluid,
 * breakpoint-varying grid needs.
 */
// Marks a region we've padded out so it holds space despite being empty — the card's SCSS
// collapses `:empty` regions by default, and this overrides that for the ones being reserved.
const RESERVED_CLASS = 'is-height-reserved';

// Every region the card stacks, in render order. Name and description are equalized separately
// rather than via their shared `__link` wrapper, so a name that wraps to two lines can't push
// its neighbour's description out of line.
const DEFAULT_REGIONS = [
  '.product-card__rating',
  '.product-card__name',
  '.product-card__desc',
  '.product-card__bullets',
  '.product-card__jrny',
  '.product-card__promo',
  '.product-card__footer',
].join(',');

class RowEqualizer extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.mutationObserver = null;
    this._scheduled = false;
    this._frame = null;
    this._lastWidth = null;
  }

  connectedCallback() {
    const attr = this.getAttribute('data-equalize') || DEFAULT_REGIONS;
    this.targets = attr
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean);

    // Descendant match, not `:scope >` — the carousel wraps each card in an <li> cell while the
    // grid renders them as direct children, and both need to work.
    this.cardSelector = this.getAttribute('data-cards') || '.product-card';

    // Re-group only when the container width changes (resize / column-count change) —
    // ignoring the height changes our own min-heights cause, which would otherwise loop.
    this.resizeObserver = new ResizeObserver(() => {
      const width = Math.round(this.clientWidth);
      if (width === this._lastWidth) return;
      this._lastWidth = width;
      this.schedule();
    });
    this.resizeObserver.observe(this);

    // Re-run once web fonts settle, since text metrics change the measured heights.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.schedule());
    }

    // Late layout + async-hydrated content (e.g. Bazaarvoice rating summaries) can change
    // heights after the first measure. Re-run on load, and watch for DOM/content mutations —
    // childList/characterData only, NOT attributes, so our own min-height writes don't loop.
    window.addEventListener('load', () => this.schedule(), { once: true });
    this.mutationObserver = new MutationObserver(() => this.schedule());
    this.mutationObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this._frame) cancelAnimationFrame(this._frame);
  }

  // Coalesce work into a single frame.
  schedule() {
    if (this._scheduled) return;
    this._scheduled = true;
    this._frame = requestAnimationFrame(() => {
      this._scheduled = false;
      this.equalize();
    });
  }

  equalize() {
    if (this.offsetParent === null) return; // hidden — skip

    const cards = Array.from(this.querySelectorAll(this.cardSelector));
    if (!cards.length) return;

    // Reset so we read natural heights, and drop any reservation from the previous pass —
    // otherwise a region that no longer needs padding would stay un-collapsed.
    cards.forEach((card) => {
      this.targets.forEach((selector) => {
        const el = card.querySelector(selector);
        if (!el) return;
        el.style.minHeight = '';
        el.classList.remove(RESERVED_CLASS);
      });
    });

    // Group cards into visual rows by their top edge, measured against this container so the
    // grouping doesn't depend on which ancestor happens to be the offsetParent (the carousel
    // nests each card in a positioned cell, the grid doesn't). Every cell in a horizontal
    // carousel track shares one top, so the whole track collapses to a single group — which is
    // exactly the "equalize across the entire track, not the visible page" the spec asks for.
    const containerTop = this.getBoundingClientRect().top;
    const rows = new Map();
    cards.forEach((card) => {
      const top = Math.round(card.getBoundingClientRect().top - containerTop);
      if (!rows.has(top)) rows.set(top, []);
      rows.get(top).push(card);
    });

    // Per row, per target: grow every card's element to the tallest in the row.
    rows.forEach((rowCards) => {
      if (rowCards.length < 2) return; // a lone card has nothing to align to
      this.targets.forEach((selector) => {
        const els = rowCards.map((card) => card.querySelector(selector)).filter(Boolean);
        if (!els.length) return;
        const max = els.reduce((tallest, el) => Math.max(tallest, el.getBoundingClientRect().height), 0);
        // Zero means no card in this row filled the region — leave every one of them collapsed
        // so an unused region costs no vertical space anywhere (E20-1460).
        if (max <= 0) return;
        els.forEach((el) => {
          el.style.minHeight = `${max}px`;
          // Empty regions are display:none by default; un-hide the ones we're padding out so
          // they actually hold the space, while untouched empties stay collapsed.
          if (!el.firstChild) el.classList.add(RESERVED_CLASS);
        });
      });
    });
  }
}

if (!customElements.get('row-equalizer')) {
  customElements.define('row-equalizer', RowEqualizer);
}
